// frontend/src/pages/MatchScoring/PlayersStrip.js
//
// Derives batter & bowler figures from recent_balls (active rows).
// For full season-level figures we'd hit /derive, but for the live strip
// the running totals are computed from the visible balls of THIS innings,
// which the state endpoint already returns (last 12 + we can rely on the
// innings totals for runs from each batter via running tallies).
//
// Approach: caller passes ALL active balls of this innings; we tally:
//   - per striker: runs_off_bat + balls_faced (every ball faced except wides —
//                  no-balls count as faced)
//   - per bowler:  legal_balls bowled + runs_conceded (runs_off_bat + wides + noballs)
//                  + wickets credited to bowler (caught/bowled/lbw/stumped/hit_wicket)
//
// Run-outs are NOT credited to the bowler (per cricket rules).

import React from 'react';
import { fmtElapsed } from './useMatchState';

function oversBowledFromBalls(legal) {
  const ov = Math.floor(legal / 6);
  const rem = legal % 6;
  return `${ov}.${rem}`;
}

function batterFigures(balls, memberId) {
  let runs = 0, faced = 0;
  for (const b of balls) {
    if (b.striker_id !== memberId) continue;
    if (b.is_penalty) continue;
    // Balls faced = every delivery except a wide. A no-ball is not a legal
    // ball for the over, but the batsman still faced it (same rule as the
    // scorecard in ballDerivation.js).
    if (!(parseInt(b.wides || 0, 10) > 0)) faced += 1;
    runs += parseInt(b.runs_off_bat || 0, 10);
  }
  return { runs, faced };
}

function bowlerFigures(balls, memberId) {
  let legal = 0, runs = 0, wkts = 0;
  for (const b of balls) {
    if (b.bowler_id !== memberId) continue;
    if (b.is_legal) legal += 1;
    runs += parseInt(b.runs_off_bat || 0, 10)
          + parseInt(b.wides       || 0, 10)
          + parseInt(b.noballs     || 0, 10);
    if (b.dismissal_type && ['bowled','caught','lbw','stumped','hit_wicket'].includes(b.dismissal_type)) {
      wkts += 1;
    }
  }
  return { legal, runs, wkts };
}

export default function PlayersStrip({
  liveState, recentBalls, membersById,
  drsByKey,
  batterFirstSeenById, now,    // Push 2 (Session 21 — 26 May 2026)
  onSwapStrike, onChangeStriker, onChangeNonStriker, onChangeBowler,
  canEdit,
}) {
  if (!liveState) return null;

  const strikerId    = liveState.striker_id;
  const nonStrikerId = liveState.non_striker_id;
  const bowlerId     = liveState.bowler_id;

  const striker     = strikerId    ? membersById.get(strikerId)    : null;
  const nonStriker  = nonStrikerId ? membersById.get(nonStrikerId) : null;
  const bowler      = bowlerId     ? membersById.get(bowlerId)     : null;

  const stFig = strikerId    ? batterFigures(recentBalls, strikerId)    : { runs: 0, faced: 0 };
  const nsFig = nonStrikerId ? batterFigures(recentBalls, nonStrikerId) : { runs: 0, faced: 0 };
  const bwFig = bowlerId     ? bowlerFigures(recentBalls, bowlerId)     : { legal: 0, runs: 0, wkts: 0 };

  // Maidens omitted in strip; figures show as O.B–R–W
  const drsStriker = strikerId ? drsByKey?.get(`${strikerId}|batter`)  : undefined;
  const drsBowler  = bowlerId  ? drsByKey?.get(`${bowlerId}|bowler`)   : undefined;

  // Push 2: time at crease for striker / non-striker. NULL first_seen_at
  // means batter just walked in and hasn't seen a ball yet — show "0:00".
  const stFirstSeen = strikerId    ? batterFirstSeenById?.get(strikerId)    : null;
  const nsFirstSeen = nonStrikerId ? batterFirstSeenById?.get(nonStrikerId) : null;
  const stTime = strikerId    ? fmtElapsed(stFirstSeen, now) : null;
  const nsTime = nonStrikerId ? fmtElapsed(nsFirstSeen, now) : null;

  return (
    <div className="sc-players">
      <div className="sc-player-row">
        <div className="sc-name">
          <span className="sc-strike-dot"></span>
          <span>{striker?.display_name || '— striker —'} *</span>
          {stTime && <span style={{ marginLeft: 6, color: 'var(--sc-text-faint)', fontVariantNumeric: 'tabular-nums' }}>— {stTime}</span>}
          {drsStriker != null && <span className="sc-drs-badge">DRS {drsStriker}</span>}
        </div>
        <div className="sc-runs">{stFig.runs}</div>
        <div className="sc-balls">{stFig.faced}</div>
        {canEdit && (
          <button className="sc-icon-btn" onClick={onSwapStrike} title="Swap strike">⇄</button>
        )}
      </div>
      <div className="sc-player-row">
        <div className="sc-name">
          <span className="sc-strike-dot invisible"></span>
          <span>{nonStriker?.display_name || '— non-striker —'}</span>
          {nsTime && <span style={{ marginLeft: 6, color: 'var(--sc-text-faint)', fontVariantNumeric: 'tabular-nums' }}>— {nsTime}</span>}
        </div>
        <div className="sc-runs">{nsFig.runs}</div>
        <div className="sc-balls">{nsFig.faced}</div>
        {canEdit && (
          <button className="sc-icon-btn" onClick={onChangeNonStriker} title="Change non-striker">edit</button>
        )}
      </div>
      <div className="sc-bowler-row" style={{ gridTemplateColumns: '1fr auto auto', columnGap: 8 }}>
        <div className="sc-name">{bowler?.display_name || '— bowler —'}</div>
        <div className="sc-figs">
          <strong>{oversBowledFromBalls(bwFig.legal)}</strong>–0–<strong>{bwFig.runs}</strong>–<strong>{bwFig.wkts}</strong>
          {drsBowler != null && <span style={{ marginLeft: 6, color: 'var(--sc-text-faint)' }}>DRS {drsBowler}</span>}
        </div>
        {canEdit && (
          <button className="sc-icon-btn" onClick={onChangeBowler} title="Change bowler">edit</button>
        )}
      </div>
    </div>
  );
}
